import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QrCode, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// Owner/super-only UPI payout editor. Any change is confirmed by an email link
// to the owner(s) before it takes effect — it's the account customers pay into.
export default function AdminUpi() {
  const auth = useAdminAuth();
  const [resto, setResto] = useState(null);
  const [form, setForm] = useState({ upi_id: "", upi_name: "" });
  const [busy, setBusy] = useState(false);
  const isOwner = ["owner", "super_admin"].includes(auth?.user?.role);

  const load = () => api.get("/admin/restaurant").then((r) => {
    setResto(r.data);
    setForm({ upi_id: r.data.upi_id || "", upi_name: r.data.upi_name || "" });
  }).catch(() => {});
  useEffect(() => { if (isOwner) load(); }, [isOwner]);

  if (!isOwner) return null; // managers never see the payout editor

  const save = async () => {
    const vpa = form.upi_id.trim();
    if (!/^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/.test(vpa)) return toast.error("Enter a valid UPI ID (e.g. name@bank)");
    setBusy(true);
    try {
      const r = await api.patch("/admin/restaurant/upi", { upi_id: vpa, upi_name: form.upi_name.trim() || null });
      toast.success(`Confirmation email sent to ${(r.data.sent_to || []).join(", ") || "the owner"} — the change applies once confirmed`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const changed = resto && (form.upi_id.trim() !== (resto.upi_id || "") || (form.upi_name.trim() || "") !== (resto.upi_name || ""));

  return (
    <div className="mt-10">
      <header className="mb-4">
        <div className="text-xs uppercase tracking-widest text-gray-400">Online payments</div>
        <h2 className="font-display text-xl font-bold tracking-tight mt-1 inline-flex items-center gap-2"><QrCode className="w-5 h-5" aria-hidden /> UPI payout</h2>
      </header>

      <div className="qd-admin-card p-5 max-w-lg space-y-3">
        <p className="text-sm text-gray-500">
          The UPI ID customers pay into when they choose <b>Pay Online</b>. They get a QR + "pay with any app" for the exact bill amount.
        </p>
        <div>
          <label className="text-xs uppercase tracking-widest text-gray-400">UPI ID</label>
          <Input data-testid="upi-id" placeholder="yourname@bank" value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} className="h-11 rounded-lg mt-1" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-gray-400">Payee name (shown to customer)</label>
          <Input data-testid="upi-name" placeholder={resto?.name || "Restaurant name"} value={form.upi_name} onChange={(e) => setForm({ ...form, upi_name: e.target.value })} className="h-11 rounded-lg mt-1" />
        </div>

        {resto?.upi_change_pending && (
          <div data-testid="upi-pending" className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-lg p-2.5">
            A change is awaiting email confirmation. The current UPI stays active until an owner clicks the link.
          </div>
        )}
        <div className="flex items-center gap-2 text-[11px] text-gray-400"><ShieldCheck className="w-3.5 h-3.5" aria-hidden /> For safety, changes take effect only after an owner confirms by email.</div>

        <Button data-testid="upi-save" onClick={save} disabled={busy || !changed} className="rounded-lg bg-[var(--qd-charcoal)] hover:bg-black text-white">
          {busy ? "Sending…" : resto?.upi_id ? "Request UPI change" : "Set up UPI"}
        </Button>
      </div>
    </div>
  );
}
